import React from 'react';
import { Eraser } from 'lucide-react';

// Pavé de signature dessinée au doigt/souris (2026-09-05, Emeni : "signé sur place par la personne qui
// reçoit la prestation") — n'existait nulle part dans l'app, qui ne connaissait jusqu'ici que la signature
// case-à-cocher ou la photo d'une signature papier uploadée (cf. rh_signature_url/contract_signature_url).
// Rendu en PNG (fond blanc) via toDataURL, cohérent avec le format déjà utilisé pour ces autres signatures.
export interface SignaturePadHandle {
    clear: () => void;
    toDataUrl: () => string | null;
}

export const SignaturePad = React.forwardRef<SignaturePadHandle, { height?: number }>(({ height = 160 }, ref) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const drawing = React.useRef(false);
    const hasStroke = React.useRef(false);
    const [isEmpty, setIsEmpty] = React.useState(true);

    const getCanvasContext = () => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        return canvas.getContext('2d');
    };

    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.scale(ratio, ratio);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, rect.width, rect.height);
            ctx.strokeStyle = '#111827';
            ctx.lineWidth = 2.2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    }, []);

    const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        const ctx = getCanvasContext();
        if (!ctx) return;
        drawing.current = true;
        const { x, y } = pointFromEvent(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing.current) return;
        const ctx = getCanvasContext();
        if (!ctx) return;
        const { x, y } = pointFromEvent(e);
        ctx.lineTo(x, y);
        ctx.stroke();
        hasStroke.current = true;
        setIsEmpty(false);
    };

    const stopDrawing = () => { drawing.current = false; };

    React.useImperativeHandle(ref, () => ({
        clear: () => {
            const canvas = canvasRef.current;
            const ctx = getCanvasContext();
            if (!canvas || !ctx) return;
            const rect = canvas.getBoundingClientRect();
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, rect.width, rect.height);
            hasStroke.current = false;
            setIsEmpty(true);
        },
        toDataUrl: () => (hasStroke.current && canvasRef.current) ? canvasRef.current.toDataURL('image/png') : null,
    }));

    return (
        <div className="space-y-1.5">
            <div className="relative rounded-xl border border-border bg-white overflow-hidden touch-none" style={{ height }}>
                <canvas
                    ref={canvasRef}
                    className="w-full h-full touch-none cursor-crosshair"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={stopDrawing}
                    onPointerLeave={stopDrawing}
                />
                {isEmpty && (
                    <span className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 pointer-events-none">
                        Signez ici avec le doigt ou la souris
                    </span>
                )}
            </div>
            <button type="button" onClick={() => (ref as React.RefObject<SignaturePadHandle>)?.current?.clear()}
                className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content">
                <Eraser size={13} /> Effacer
            </button>
        </div>
    );
});
SignaturePad.displayName = 'SignaturePad';
